import { useAppKitConnection } from '@reown/appkit-adapter-solana/react';
import { PublicKey } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';

import { Currency } from '@/constants';
import useL1TokenContractParams from '@/hooks/useL1TokenContractParams';
import { isSplCurrency } from '@/utils';

import { getSolanaNetwork } from '../utils/rpc';

export const useReadSplBalance = (address: string | null, token: Currency) => {
  const { connection } = useAppKitConnection();
  const { getContractParams } = useL1TokenContractParams();
  const network = getSolanaNetwork();

  return useQuery({
    queryKey: ['spl-balance', address, token, network.id],
    queryFn: async () => {
      if (!address || !isSplCurrency(token) || !connection) {
        return 0n;
      }

      const { L1_TOKEN_ADDRESS } = getContractParams(token);

      if (!L1_TOKEN_ADDRESS) {
        return 0n;
      }

      const owner = new PublicKey(address);
      const mint = new PublicKey(L1_TOKEN_ADDRESS);

      const tokenAccounts = await connection.getParsedTokenAccountsByOwner(owner, { mint });
      const balance = tokenAccounts.value.reduce((sum, account) => {
        const amount = account.account.data.parsed.info.tokenAmount.amount;
        return sum + BigInt(amount);
      }, 0n);

      return balance;
    },
    enabled: !!address && isSplCurrency(token) && !!connection,
    refetchInterval: 10_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};
